import React from 'react';
import { useForm } from 'antd/lib/form/Form';
import { Form, notification as Notification } from 'antd';
import { EInvoiceButton } from '../../../components/button';
import { EInvoiceForm } from '../../../components/form/form';

import {
    EInvoiceInput,
    EInvoiceInputPassword,
} from '../../../components/form/input';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ICCheckFill, ICClose, ICWarning } from '../../../icons';
import { setLoading } from '../../../redux/slices/appInfo';
import { login } from '../../../api/auth';
import { setUserInfo, TUserInfo } from '../../../redux/slices/userInfo';
import { ApiInstance } from '../../../api';
import { formatSearch } from '../../../utils/formatSearch';

type NotificationType = 'success' | 'info' | 'warning' | 'error';

const Wrapper = styled.div`
    background-color: rgba(0, 0, 0, 0.3);
    background-image: url('/images/background.png');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    .circular-3d-text {
        font-size: 40px;
        font-weight: 900;
        position: relative;
        background: linear-gradient(45deg, #088947, #088947);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        text-transform: uppercase;
        letter-spacing: 2px;
        text-shadow:
            0 1px 0 white,
            0 2px 0 white,
            0 3px 0 #bbb,
            0 4px 0 #aaa,
            0 5px 0 #999,
            0 6px 0 #5FB33C,
            0 7px 0 #5FB33C,
            0 8px 0 #5FB33C,
            0 9px 0 #5FB33C,
            0 10px 10px rgba(0, 0, 0, 0.4),
            0 20px 20px rgba(0, 0, 0, 0.25),
            0 30px 30px rgba(0, 0, 0, 0.15);
        transition: all 0.3s ease;
    }
    .circular-3d-text span {
        position: absolute;
        left: 50%;
        transform-origin: 0 120px;
        transition: transform 0.3s ease;
    }

    .circular-3d-text:hover span {
        text-shadow:
            0 1px 0 #ccc,
            0 2px 0 #ccc,
            0 3px 0 #bbb,
            0 4px 0 #aaa,
            0 5px 0 #999,
            0 6px 0 #888,
            0 7px 0 #777,
            0 15px 15px rgba(0, 0, 0, 0.5);
        transform-origin: 0 130px;
    }

    @media (max-width: 768px) {
        .circular-3d-text {
            font-size: 2rem;
        }
        .circular-3d-text span {
            transform-origin: 0 80px;
        }
    }

    @media (max-width: 480px) {
        .circular-3d-text {
            font-size: 1.5rem;
        }
        .circular-3d-text span {
            transform-origin: 0 60px;
        }
    }
`;

type FieldType = {
    username?: string;
    password?: string;
};

export const LoginPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loginForm] = useForm();
    const [api, contextHolder] = Notification.useNotification();
    const openNotification = (
        type: NotificationType,
        title: React.ReactNode,
        description?: React.ReactNode
    ) => {
        if (type === 'success') {
            api[type]({
                icon: <ICCheckFill />,
                message: (
                    <span className="text-high-em text-body-bold">{title}</span>
                ),
                description,
                placement: 'bottom',
                className: 'middle-notification',
                closeIcon: <ICClose height={18} width={18} fill={'#4F4B5C'} />,
            });
        }
        if (type === 'error') {
            return api[type]({
                icon: <ICWarning fill="#F03D3D" />,
                message: (
                    <span className="text-body-bold text-high-em">{title}</span>
                ),
                description,
                placement: 'bottom',
                className: 'middle-notification',
                closeIcon: <ICClose height={18} width={18} fill={'#4F4B5C'} />,
            });
        }
    };

    const onFinished = React.useCallback(async () => {
        try {
            dispatch(setLoading(true));
            const values = await loginForm.validateFields();
            const response = await login(values);
            if (response.status === 200) {
                const { data } = response.data || {};
                localStorage.clear();
                const newUserInfo: TUserInfo = {
                    username: data?.username,
                    refreshToken: data?.rt,
                    expires_in: data?.expires_in,
                };
                ApiInstance.setTokens(data?.at, data?.rt);
                newUserInfo.token = data?.at;
                dispatch(setUserInfo(newUserInfo));
                localStorage.setItem('userInfo', JSON.stringify(newUserInfo));
                localStorage.setItem('token', data?.at);
                openNotification('success', 'Login success');
                setTimeout(() => {
                    const search = formatSearch(location.search);
                    const from = search.from || '/dashboard';
                    navigate(from);
                }, 5);
            }
        } catch (error) {
            openNotification(
                'error',
                'Login failed',
                'Please check your email and password'
            );
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, navigate]);

    return (
        <Wrapper className="flex gap-[20px] flex-col w-screen h-screen items-center justify-center bg-surface-low">
            {contextHolder}
            <div className="shadow-big-shadow max-md:max-w-[478px] max-md:w-auto w-[600px] p-[32px] flex flex-col gap-[24px] rounded-[12px] overflow-hidden box-border bg-white">
                <div className="flex flex-col justify-center">
                    <div className="flex items-center justify-center text-center w-full">
                        <h1 className="circular-3d-text">
                            Eco-Sign OFIS Middleware
                        </h1>
                    </div>
                </div>
                <EInvoiceForm
                    form={loginForm}
                    name="loginForm"
                    layout="vertical"
                    labelCol={{ span: 8 }}
                    onFinish={onFinished}
                    childNode={
                        <>
                            <div className="form flex flex-col w-full gap-[16px]">
                                <div>
                                    <Form.Item<FieldType>
                                        label="Email"
                                        name="username"
                                        rules={[
                                            {
                                                required: true,
                                                whitespace: true,
                                                message:
                                                    'Please input your email!',
                                            },
                                        ]}
                                    >
                                        <EInvoiceInput
                                            size="large"
                                            maxLength={50}
                                            placeholder="Email"
                                        />
                                    </Form.Item>
                                </div>
                                <div>
                                    <Form.Item<FieldType>
                                        label="Password"
                                        name="password"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please input your password!',
                                            },
                                        ]}
                                    >
                                        <EInvoiceInputPassword
                                            size="large"
                                            maxLength={20}
                                            placeholder="Password"
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                            <div className="flex flex-col w-full gap-[24px] pt-[32px]">
                                <div>
                                    <EInvoiceButton htmlType="submit">
                                        Login
                                    </EInvoiceButton>
                                </div>
                            </div>
                        </>
                    }
                />
            </div>
        </Wrapper>
    );
};
